import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceResultCard } from '@/components/AbsenceApplication/AbsenceResultCard';
import { useNonAttendanceApplication } from '@/components/AbsenceApplication/hooks/useNonAttendanceApplication';
import { useRetryCateringCancellation } from '@/components/AbsenceApplication/hooks/useRetryCateringCancellation';
import type { NonAttendanceApplicationDetail } from '@/components/AbsenceApplication/types';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ModalVariant } from '@/types/components';

interface Props {
  isOpen?: boolean;
  application: NonAttendanceApplicationDetail;
  onClose: () => void;
  onTimeout: () => void;
}

export const AbsenceResultModal: React.FC<Props> = ({
  application,
  onClose,
  onTimeout,
}) => {
  const { t } = useTranslation();
  const { data, isPolling } = useNonAttendanceApplication({
    id: application.id,
    initialData: application,
    onTimeout,
  });
  const { mutate: retryCatering, isPending: isRetrying } =
    useRetryCateringCancellation();
  const [hasRetried, setHasRetried] = useState(false);
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);

  const currentApplication = data ?? application;
  const showSpinner = isPolling && !hasRetried;
  const isCardLoading = isRetrying || (isPolling && hasRetried);

  const handleRetry = () => {
    if (!currentApplication) {
      return;
    }
    setHasRetried(true);
    retryCatering(currentApplication.id);
  };

  return (
    <Modal
      isOpen
      label={t('absenceApplication.result.title')}
      variant={ModalVariant.BottomSheet}
      className="md:h-auto md:w-[400px] md:max-h-[calc(100svh-80px)]"
      contentRef={sheetRef}
      onClose={onClose}
    >
      <div
        {...swipeProps}
        className="flex shrink-0 flex-col max-md:cursor-grab max-md:touch-none max-md:active:cursor-grabbing"
      >
        <div className="mx-auto mt-2 h-[3px] w-10 rounded-full bg-drag-handle md:hidden" />
        <div className="flex items-center gap-3 bg-background px-4 pt-5 pb-3 md:px-5 md:pb-4">
          <h2 className="flex-1 text-foreground text-[18px] leading-6 font-semibold md:text-headline-20">
            {t('absenceApplication.result.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('global.close')}
            className="hidden size-4 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6">
        {showSpinner && (
          <div className="flex flex-1 items-center justify-center">
            <PageSpinner className="size-[20px] text-subtle-foreground" />
          </div>
        )}
        {!showSpinner && currentApplication && (
          <AbsenceResultCard
            application={currentApplication}
            isRetrying={isCardLoading}
            onRetry={handleRetry}
          />
        )}
      </div>
    </Modal>
  );
};
